import { Injectable } from '@angular/core';
import { LocalStorageService } from '../infrastructure/storage/local-storage.service';

/**
 * User information stored in localStorage
 */
export interface UserInfo {
  id?: number;
  username?: string;
  fullname?: string;
  firstname?: string;
  lastname?: string;
  picture?: string | null;
  avatar?: string | null;
  roles?: string[];
}

/**
 * Centralized authentication state management service.
 *
 * Provides a single source of truth for authentication state,
 * abstracting  localStorage operations and ensuring type safety.
 *
 * This service replaces direct localStorage usage throughout the app
 * for better maintainability, testability, and security.
 *
 * @example
 * ```typescript
 * constructor(private authState: AuthStateService) {}
 *
 * // Check auth status
 * if (authState.isAuthenticated()) {
 *   const user = this.authState.getUserInfo();
 * }
 *
 * // Clear on logout
 * this.authState.clearAuthState();
 * ```
 */
@Injectable({
  providedIn: 'root',
})
export class AuthStateService {
  private readonly authKey = 'authenticated';
  private readonly userInfoKey = 'userInfo';

  constructor(private readonly storage: LocalStorageService) {}

  /**
   * Check if user is authenticated
   * @returns true if authenticated flag is set
   */
  isAuthenticated(): boolean {
    const value = this.storage.getItem<string>(this.authKey);
    return value === 'true';
  }

  /**
   * Set authentication status
   * @param value - Authentication status
   */
  setAuthenticated(value: boolean): void {
    this.storage.setItem(this.authKey, value ? 'true' : 'false');
  }

  /**
   * Get stored user information
   * @returns User info or null if not found
   */
  getUserInfo(): UserInfo | null {
    return this.storage.getItem<UserInfo>(this.userInfoKey);
  }

  /**
   * Store user information
   * @param info - User information to store
   */
  setUserInfo(info: UserInfo): void {
    this.storage.setItem(this.userInfoKey, info);
  }

  /**
   * Clear all authentication state
   * Should be called on logout
   */
  clearAuthState(): void {
    this.storage.deleteItem(this.authKey);
    this.storage.deleteItem(this.userInfoKey);
  }

  /**
   * Clear only user info (keep authenticated flag)
   * Useful for partial state updates
   */
  clearUserInfo(): void {
    this.storage.deleteItem(this.userInfoKey);
  }

  setExpiration(expiresAt: number): void {
    this.storage.setItem('token_expires_at', expiresAt.toString());
  }

  getExpiration(): number | null {
    const exp = this.storage.getItem<string>('token_expires_at');
    return exp ? Number.parseInt(exp, 10) : null;
  }
}
